// client/src/components/pages/AmendmentsAllocateTransactions/FollowSourceParts.tsx
//
// ST-00212: small pieces the Source updates, Unmatched corrections and Source follow report tabs
// share (pager, message banners, table cell classes). Theme-token colours only.
import type { ReactNode } from 'react';
import { formatCount } from './followSourceFormat';

export const TH =
  'px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap';
export const TD = 'px-3 py-2 align-top text-xs text-slate-700';
export const TABLE = 'min-w-full divide-y divide-slate-200';
export const THEAD = 'bg-slate-50';
export const TBODY = 'divide-y divide-slate-100 bg-surface';

export const BUTTON =
  'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';
export const BUTTON_PRIMARY = `${BUTTON} bg-primary-600 text-white border-primary-600 hover:bg-primary-700`;
export const BUTTON_SECONDARY = `${BUTTON} bg-surface text-slate-700 border-slate-300 hover:border-slate-400`;

/** A red (error), green (done) or plain (info) line above a list. */
export function Banner({
  tone,
  children,
  onDismiss,
}: {
  tone: 'error' | 'success' | 'info';
  children: ReactNode;
  onDismiss?: () => void;
}) {
  const cls =
    tone === 'error'
      ? 'bg-red-50 border-red-200 text-red-700'
      : tone === 'success'
        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
        : 'bg-slate-50 border-slate-200 text-slate-600';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`mx-4 mt-3 p-2.5 border text-xs rounded-md flex items-start gap-2 ${cls}`}
    >
      <span className="flex-1">{children}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="text-current opacity-70 hover:opacity-100"
        >
          ×
        </button>
      )}
    </div>
  );
}

/** "Showing 1–100 of 340" with Previous / Next, for an offset-paged list. */
export function ListPager({
  offset,
  pageSize,
  shown,
  total,
  onOffsetChange,
}: {
  offset: number;
  pageSize: number;
  shown: number;
  total: number;
  onOffsetChange: (offset: number) => void;
}) {
  if (total <= pageSize && offset === 0) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-slate-200 text-xs text-slate-500">
      <span className="tabular-nums">
        Showing {formatCount(shown ? offset + 1 : 0)}–{formatCount(offset + shown)} of{' '}
        {formatCount(total)}
      </span>
      <span className="flex gap-2">
        <button
          type="button"
          className={BUTTON_SECONDARY}
          disabled={offset === 0}
          onClick={() => onOffsetChange(Math.max(0, offset - pageSize))}
        >
          Previous
        </button>
        <button
          type="button"
          className={BUTTON_SECONDARY}
          disabled={offset + shown >= total}
          onClick={() => onOffsetChange(offset + pageSize)}
        >
          Next
        </button>
      </span>
    </div>
  );
}

/** Loading / empty line in place of a table body. */
export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-8 text-center text-xs text-slate-400">
        {children}
      </td>
    </tr>
  );
}
